import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  getPriorAnalyses,
  getScan,
  manuallyEnqueueScanForAnalysis,
  type AdminScanDetail,
  type PriorAnalysisSummary,
} from '../lib/api';
import type { ScanAnalysisReview, ScanAnalysisReviewStatus } from '@obd-reader/shared';
import {
  ProposedAppChangeResolutionControl,
  ResolutionBadge,
} from '../components/ProposedAppChangeResolutionControl';
import { theme } from '../theme';

export function ScanDetailPage() {
  const { scanId = '' } = useParams<{ scanId: string }>();
  const [scan, setScan] = useState<AdminScanDetail | null>(null);
  const [priorAnalyses, setPriorAnalyses] = useState<PriorAnalysisSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [analyseReason, setAnalyseReason] = useState('');
  const [analyseState, setAnalyseState] = useState<
    | { kind: 'idle' }
    | { kind: 'sending' }
    | { kind: 'sent'; enqueuedAt: string }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  useEffect(() => {
    setScan(null);
    setPriorAnalyses([]);
    setAnalyseState({ kind: 'idle' });
    setAnalyseReason('');
    getScan(scanId)
      .then(setScan)
      .catch((err: Error) => setError(err.message));
    // Prior analyses load independently — the main page renders the
    // moment the scan itself arrives; the prior-analyses section appears
    // when its fetch resolves. Best-effort: a failure here just leaves
    // the section unrendered (no error UI clutter).
    getPriorAnalyses(scanId)
      .then((res) => setPriorAnalyses(res.analyses))
      .catch(() => {});
  }, [scanId]);

  const sendForAnalysis = async () => {
    setAnalyseState({ kind: 'sending' });
    try {
      const res = await manuallyEnqueueScanForAnalysis(scanId, analyseReason);
      setAnalyseState({ kind: 'sent', enqueuedAt: res.enqueuedAt });
    } catch (err) {
      setAnalyseState({ kind: 'error', message: (err as Error).message });
    }
  };

  if (error) return <p style={{ color: theme.status.error }}>Error: {error}</p>;
  if (!scan) return <p>Loading…</p>;

  return (
    <div>
      <Link to="/scans">← back to scans</Link>
      <h1 style={{ marginTop: 12 }}>
        {scan.scanShortcodeBody ? `Scan OB-${scan.scanShortcodeBody}` : `Scan ${scan.scanId}`}
      </h1>
      {scan.scanShortcodeBody && (
        <p
          style={{
            marginTop: 0,
            fontSize: 12,
            color: theme.text.tertiary,
            fontFamily: 'monospace',
          }}
        >
          internal: {scan.scanId}
        </p>
      )}
      {scan.outcome === 'success' && (
        <ManualAnalyseControl
          reason={analyseReason}
          onReasonChange={setAnalyseReason}
          state={analyseState}
          onSend={() => void sendForAnalysis()}
        />
      )}
      {scan.review && (
        <ReviewStatusSection
          scanId={scan.scanId}
          review={scan.review}
          onReviewUpdated={(review) => setScan((prev) => (prev ? { ...prev, review } : prev))}
        />
      )}
      {priorAnalyses.length > 0 && <PriorAnalysesSection analyses={priorAnalyses} />}
      <dl style={styles.dl}>
        <Row label="Scanned at" value={formatTimestamp(scan.scannedAt)} />
        <Row label="Outcome" value={scan.outcome} />
        <Row label="VIN" value={scan.vin ?? '—'} mono />
        {(scan.reportUrl || scan.reportUrlLatest) && (
          <Row
            label="Report PDF"
            value={
              <span style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {scan.reportUrlLatest && (
                  <a href={scan.reportUrlLatest} target="_blank" rel="noreferrer">
                    Open latest
                  </a>
                )}
                {scan.reportUrl && (
                  <a href={scan.reportUrl} target="_blank" rel="noreferrer">
                    Open this scan's snapshot
                  </a>
                )}
              </span>
            }
          />
        )}
        {(scan.batteryReportUrl || scan.batteryReportUrlLatest) && (
          <Row
            label="Battery Condition PDF"
            value={
              <span style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {scan.batteryReportUrlLatest && (
                  <a href={scan.batteryReportUrlLatest} target="_blank" rel="noreferrer">
                    Open latest
                  </a>
                )}
                {scan.batteryReportUrl && (
                  <a href={scan.batteryReportUrl} target="_blank" rel="noreferrer">
                    Open this scan's snapshot
                  </a>
                )}
              </span>
            }
          />
        )}
      </dl>

      {scan.summary && (
        <>
          <h2>Summary</h2>
          <CodeBlock data={scan.summary} />
        </>
      )}
      {scan.failureReport && (
        <>
          <h2>Failure report</h2>
          <CodeBlock data={scan.failureReport} />
        </>
      )}
      {scan.enriched && (
        <>
          <h2>Raw enriched payload</h2>
          <CodeBlock data={scan.enriched} />
        </>
      )}
    </div>
  );
}

type AnalyseState =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'sent'; enqueuedAt: string }
  | { kind: 'error'; message: string };

function ManualAnalyseControl({
  reason,
  onReasonChange,
  state,
  onSend,
}: {
  reason: string;
  onReasonChange: (value: string) => void;
  state: AnalyseState;
  onSend: () => void;
}) {
  const disabled = state.kind === 'sending';
  return (
    <div
      style={{
        marginTop: 12,
        padding: '10px 14px',
        border: `1px solid ${theme.surface.border}`,
        borderRadius: theme.radius.md,
        background: theme.surface.panel,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13 }}>Send for AI analysis</strong>
        <span style={{ color: theme.text.tertiary, fontSize: 12 }}>
          Bypasses the global trigger mode. Idempotent — duplicate clicks won't requeue.
        </span>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
        <input
          type="text"
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          placeholder="Optional reason (e.g. 'customer complaint — re-check')"
          disabled={disabled}
          style={{
            flex: 1,
            padding: '6px 10px',
            background: theme.surface.header,
            color: theme.text.primary,
            border: `1px solid ${theme.surface.borderStrong}`,
            borderRadius: theme.radius.md,
            fontSize: 13,
          }}
        />
        <button
          onClick={onSend}
          disabled={disabled}
          style={{
            padding: '6px 16px',
            background: theme.brand.primary,
            color: theme.text.onBrand,
            border: 'none',
            borderRadius: theme.radius.md,
            fontSize: 13,
            fontWeight: 600,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
          }}
        >
          {state.kind === 'sending' ? 'Sending…' : 'Analyse'}
        </button>
      </div>
      {state.kind === 'sent' && (
        <span style={{ color: theme.status.success, fontSize: 12 }}>
          Enqueued at {new Date(state.enqueuedAt).toLocaleString()}. The analysis will appear in the
          Analysis Review queue once the LLM completes.
        </span>
      )}
      {state.kind === 'error' && (
        <span style={{ color: theme.status.error, fontSize: 12 }}>{state.message}</span>
      )}
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <>
      <dt style={{ color: theme.text.secondary }}>{label}</dt>
      <dd style={{ margin: 0, fontFamily: mono ? theme.fonts.mono : 'inherit' }}>{value}</dd>
    </>
  );
}

function CodeBlock({ data }: { data: unknown }) {
  return <pre style={styles.pre}>{JSON.stringify(data, null, 2)}</pre>;
}

function formatTimestamp(value: string): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return `${d.toLocaleString()} (${value})`;
}

const REVIEW_STATUS_PALETTE: Record<
  ScanAnalysisReviewStatus,
  { bg: string; fg: string; label: string }
> = {
  'pending-review': { bg: theme.status.warning, fg: '#3a2200', label: 'Pending review' },
  'analysis-failed': { bg: theme.status.error, fg: '#3a0000', label: 'Analysis failed' },
  approved: { bg: theme.status.success, fg: '#013220', label: 'Approved' },
  rejected: { bg: theme.surface.borderStrong, fg: theme.text.primary, label: 'Rejected' },
};

function ReviewStatusSection({
  scanId,
  review,
  onReviewUpdated,
}: {
  scanId: string;
  review: ScanAnalysisReview;
  onReviewUpdated: (review: ScanAnalysisReview) => void;
}) {
  const palette = REVIEW_STATUS_PALETTE[review.status];
  // 'pending-review' and 'analysis-failed' are the actionable states that
  // appear in the queue page. For 'approved' / 'rejected' we show the
  // outcome inline (no actionable destination).
  const isActionable = review.status === 'pending-review' || review.status === 'analysis-failed';
  return (
    <div
      style={{
        marginTop: 12,
        padding: '12px 14px',
        border: `1px solid ${theme.surface.border}`,
        borderRadius: theme.radius.md,
        background: theme.surface.panel,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13 }}>AI Analysis</strong>
        <span
          style={{
            background: palette.bg,
            color: palette.fg,
            padding: '2px 8px',
            borderRadius: theme.radius.sm,
            fontWeight: 700,
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: 0.4,
          }}
        >
          {palette.label}
        </span>
        <span style={{ color: theme.text.tertiary, fontSize: 12 }}>
          Analysed {formatTimestamp(review.analysedAt)} · model {review.model}
        </span>
        {isActionable && (
          <Link to="/scan-analysis-queue" style={{ marginLeft: 'auto', fontSize: 13 }}>
            Open in Analysis Review →
          </Link>
        )}
      </div>

      {review.status === 'analysis-failed' && review.analysisError && (
        <div style={{ color: theme.status.error, fontSize: 12, fontFamily: theme.fonts.mono }}>
          {review.analysisError.slice(0, 400)}
          {review.analysisError.length > 400 ? '…' : ''}
        </div>
      )}

      {review.diagnosis && (
        <div style={{ fontSize: 13, color: theme.text.primary, whiteSpace: 'pre-wrap' }}>
          {review.diagnosis}
        </div>
      )}

      {review.proposedCommands.length > 0 && (
        <div style={{ fontSize: 12, color: theme.text.secondary }}>
          {review.proposedCommands.length} proposed command
          {review.proposedCommands.length === 1 ? '' : 's'}
          {review.proposedAppChanges && review.proposedAppChanges.length > 0
            ? ` · ${review.proposedAppChanges.length} proposed app change${review.proposedAppChanges.length === 1 ? '' : 's'}`
            : ''}
        </div>
      )}

      {review.proposedAppChanges && review.proposedAppChanges.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <strong style={{ fontSize: 12, color: theme.text.secondary }}>
            Proposed app-level changes
          </strong>
          {review.proposedAppChanges.map((change, idx) => (
            <div
              key={idx}
              style={{
                padding: '8px 10px',
                background: theme.surface.header,
                border: `1px solid ${theme.surface.border}`,
                borderRadius: theme.radius.sm,
                fontSize: 12,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                <code
                  style={{
                    fontFamily: theme.fonts.mono,
                    fontSize: 10,
                    padding: '1px 5px',
                    background: theme.surface.panel,
                    borderRadius: 3,
                  }}
                >
                  {change.category}
                </code>
                <strong>{change.summary}</strong>
                <span
                  style={{
                    fontSize: 11,
                    color:
                      change.priority === 'high'
                        ? theme.status.error
                        : change.priority === 'medium'
                          ? theme.status.warning
                          : theme.text.tertiary,
                  }}
                >
                  ({change.priority})
                </span>
              </div>
              <div style={{ marginTop: 4, color: theme.text.secondary }}>{change.details}</div>
              <ProposedAppChangeResolutionControl
                scanId={scanId}
                index={idx}
                change={change}
                onResolved={(updated) => onReviewUpdated(updated.review)}
              />
            </div>
          ))}
        </div>
      )}

      {(review.status === 'approved' || review.status === 'rejected') && (
        <div style={{ fontSize: 12, color: theme.text.tertiary, display: 'flex', gap: 12 }}>
          <span>
            {review.status === 'approved' ? 'Approved' : 'Rejected'}
            {review.reviewedAt ? ` ${formatTimestamp(review.reviewedAt)}` : ''}
            {review.reviewedBy ? ` by ${review.reviewedBy}` : ''}
          </span>
        </div>
      )}

      {review.reviewerNote && (
        <div
          style={{
            fontSize: 12,
            color: theme.text.primary,
            background: theme.surface.header,
            borderLeft: `3px solid ${theme.surface.borderStrong}`,
            padding: '6px 10px',
            borderRadius: theme.radius.sm,
          }}
        >
          <strong style={{ color: theme.text.tertiary }}>Reviewer note: </strong>
          {review.reviewerNote}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  dl: {
    display: 'grid',
    gridTemplateColumns: 'max-content 1fr',
    gap: '4px 16px',
    fontSize: 13,
    marginBottom: 24,
  },
  pre: {
    background: theme.surface.panel,
    border: `1px solid ${theme.surface.border}`,
    borderRadius: theme.radius.lg,
    padding: 12,
    fontSize: 12,
    overflow: 'auto',
    maxHeight: 600,
    fontFamily: theme.fonts.mono,
  },
};

const TIER_LABEL: Record<PriorAnalysisSummary['tier'], string> = {
  exact: 'Exact match',
  'year-near': 'Sibling (±2yr)',
  'year-any': 'Sibling',
};

function PriorAnalysesSection({ analyses }: { analyses: PriorAnalysisSummary[] }) {
  const exactCount = analyses.filter((a) => a.tier === 'exact').length;
  const siblingCount = analyses.length - exactCount;
  return (
    <div
      style={{
        marginTop: 12,
        padding: '12px 14px',
        border: `1px solid ${theme.surface.border}`,
        borderRadius: theme.radius.md,
        background: theme.surface.panel,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 8 }}>
        <strong style={{ fontSize: 13 }}>Prior AI analyses for this vehicle</strong>
        <span style={{ color: theme.text.tertiary, fontSize: 12 }}>
          {exactCount} exact match{exactCount === 1 ? '' : 'es'}
          {siblingCount > 0 ? ` · ${siblingCount} sibling${siblingCount === 1 ? '' : 's'}` : ''}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {analyses.map((a) => (
          <PriorAnalysisRow key={a.scanId} analysis={a} />
        ))}
      </div>
    </div>
  );
}

function PriorAnalysisRow({ analysis }: { analysis: PriorAnalysisSummary }) {
  const palette = REVIEW_STATUS_PALETTE[analysis.status];
  return (
    <div
      style={{
        padding: '10px 12px',
        background: theme.surface.header,
        border: `1px solid ${theme.surface.border}`,
        borderRadius: theme.radius.md,
        fontSize: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span
          style={{
            background: palette.bg,
            color: palette.fg,
            padding: '1px 6px',
            borderRadius: theme.radius.sm,
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.3,
          }}
        >
          {palette.label}
        </span>
        <span
          style={{
            color: theme.text.tertiary,
            fontSize: 11,
            padding: '1px 6px',
            border: `1px solid ${theme.surface.borderStrong}`,
            borderRadius: theme.radius.sm,
          }}
        >
          {TIER_LABEL[analysis.tier]}
        </span>
        <Link
          to={`/scans/${encodeURIComponent(analysis.scanId)}`}
          style={{ fontSize: 12, fontFamily: theme.fonts.mono }}
        >
          {analysis.scanId.slice(0, 8)}…
        </Link>
        <span style={{ color: theme.text.tertiary, marginLeft: 'auto' }}>
          {formatTimestamp(analysis.analysedAt)} · model {analysis.model}
        </span>
      </div>
      {analysis.diagnosisExcerpt && (
        <div style={{ marginTop: 6, color: theme.text.primary }}>{analysis.diagnosisExcerpt}</div>
      )}
      {analysis.proposedAppChangeSummaries.length > 0 && (
        <ul style={{ margin: '6px 0 0', paddingLeft: 18, color: theme.text.secondary }}>
          {analysis.proposedAppChangeSummaries.map((s, i) => (
            <li key={i} style={{ marginBottom: 4 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                <code
                  style={{
                    fontFamily: theme.fonts.mono,
                    fontSize: 10,
                    padding: '1px 5px',
                    background: theme.surface.panel,
                    borderRadius: 3,
                  }}
                >
                  {s.category}
                </code>
                <span>{s.summary}</span>
                {s.resolution && (
                  <ResolutionBadge resolution={s.resolution} note={s.resolutionNote} />
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {analysis.reviewerNote && (
        <div
          style={{
            marginTop: 6,
            padding: '4px 8px',
            background: theme.surface.panel,
            borderLeft: `3px solid ${theme.surface.borderStrong}`,
            borderRadius: theme.radius.sm,
          }}
        >
          <strong style={{ color: theme.text.tertiary }}>Reviewer note: </strong>
          {analysis.reviewerNote}
          {analysis.reviewedByDisplayName ? ` — ${analysis.reviewedByDisplayName}` : ''}
        </div>
      )}
    </div>
  );
}
